import { BCMSDate } from "bcms-react";
import { Transaction } from "model";

/**
 * @class ACHSearch
 * @classdesc Class definition for controlling the properties that are set in a search feature.
 * 
 * @property {STRING} _companyID - The company id to filter by
 * @property {STRING} _batch - The batch to filter by
 * @property {STRING} _schedule - The schedule to filter by. Note, this filter is only used in the "Scheduled ACH Batches" widget.
 * @property {STRING} _status - The status to filter by
 * @property {BOOLEAN} _prenote - The prenote to filter by
 * @property {STRING} _referenceNumber - The referenceNumber to filter by
 * @property {STRING} _offsetAccountID - The offsetAccountID to filter by
 * @property {STRING} _transactionType - The transactionType to filter by
 * @property {STRING} _effectiveDate - The effectiveDate to filter by
 * @property {BCMSDATE} _effectiveDateFromDate - The effectiveDateFromDate to filter by
 * @property {BCMSDATE} _effectiveDateToDate - The effectiveDateToDate to filter by
 * @property {STRING} _lastUpdatedDate - The lastUpdatedDate to filter by
 * @property {BCMSDATE} _lastUpdatedDateFromDate - The lastUpdatedDateFromDate to filter by
 * @property {BCMSDATE} _lastUpdatedDateToDate - The lastUpdatedDateToDate to filter by
 */
export default class ACHSearch {
    _companyID;
    _batch;
    _schedule;
    _status;
    _prenote;
    _referenceNumber;
    _offsetAccountID;
    _transactionType;
    _effectiveDate;
    _effectiveDateFromDate;
    _effectiveDateToDate;
    _lastUpdatedDate;
    _lastUpdatedDateFromDate;
    _lastUpdatedDateToDate;

    _statuses = new Map([ 
        [ "pending approval", "Pending Approval" ],
        [ "approved", "Approved" ],
        [ "rejected", "Rejected" ],
        [ "canceled", "Canceled" ],
        [ "processing", "Processing" ],
        [ "processed", "Processed" ],
        [ "canceled by system", "Canceled By System" ],
        [ "processing failed", "Processing Failed" ]
    ]);

    _transactionTypes = new Map([
        [ "ppd credit - consumer credit", "PPD Credit - Consumer Credit" ],
        [ "ppd debit - consumer debit", "PPD Debit - Consumer Debit" ],
        [ "ccd credit - non-consumer credit", "CCD Credit - Non-Consumer Credit " ],
        [ "ccd debit - non-consumer debit", "CCD Credit - Non-Consumer Credit " ],
        [ "tax payments", "Tax Payments" ],
        [ "rck - re-presentation check", "RCK - Re-Presentation Check" ],
        [ "web - consumer debit", "WEB - Consumer Debit" ],
        [ "tel - telephone-initiated debit", "TEL - Telephone-Initiated Debit" ],
        [ "ctx debit - corporate trade exchange debit", "CTX Debit - Corporate Trade Exchange Debit" ],
        [ "ctx credit - corporate trade exchange credit", "CTX Credit - Corporate Trade Exchange Credit" ],
        [ "child support payments", "Child Support Payments" ],
    ]);

    _dateRanges = new Map([
        [ "custom", "Custom" ],
        [ "today", "Today" ],
        [ "yesterday", "Yesterday" ],
        [ "tomorrow", "Tomorrow" ],
        [ "this week", "This Week" ],
        [ "last week", "Last Week" ],
        [ "next week", "Next Week" ],
        [ "this month", "This Month" ],
        [ "last month", "Last Month" ],
        [ "next month", "Next Month" ],
        [ "last 3 months", "Last 3 Months" ],
        [ "last 6 months", "Last 6 Months" ],
        [ "last 9 months", "Last 9 Months" ],
        [ "this year", "This Year" ],
        [ "last year", "Last Year" ],
        [ "next year", "next year" ],
        [ "all dates", "All Dates" ],
    ]);


    constructor(record) {
        this.companyID = record.companyID;
        this.batch = record.batch;
        this.schedule = record.schedule;
        this.status = record.status;
        this.prenote = record.prenote;
        this.referenceNumber = record.referenceNumber;
        this.offsetAccountID = record.offsetAccountID;
        this.transactionType = record.transactionType;
        this.effectiveDate = record.effectiveDate;
        this.effectiveDateFromDate = record.effectiveDateFromDate;
        this.effectiveDateToDate = record.effectiveDateToDate;
        this.lastUpdatedDate = record.lastUpdatedDate;
        this.lastUpdatedDateFromDate = record.lastUpdatedDateFromDate;
        this.lastUpdatedDateToDate = record.lastUpdatedDateToDate;
    }

    /**
     * @name companyID
     * @instance
     * @property set {STRING} - sets the company id
     * @memberof ACHSearch
     */
    set companyID(value) { this._companyID = value; }

    /**
     * @name batch
     * @instance
     * @property set {STRING} - sets the batch
     * @memberof ACHSearch
     */
    set batch(value) { this._batch = value; }

    /**
     * @name schedule
     * @instance
     * @property set {STRING} - sets the schedule
     * @memberof ACHSearch
     */
    set schedule(value) { this._schedule = value; }

    /**
     * @name status
     * @instance
     * @property set {STRING} - sets the status
     * @memberof ACHSearch
     */
    set status(value) { this._status = value; }

    /**
     * @name prenote
     * @instance
     * @property set {STRING} - sets the prenote
     * @memberof ACHSearch
     */
    set prenote(value) { this._prenote = value; }

    /**
     * @name referenceNumber
     * @instance
     * @property set {STRING} - sets the referenceNumber
     * @memberof ACHSearch
     */
    set referenceNumber(value) { this._referenceNumber = value; }

    /**
     * @name offsetAccountID
     * @instance
     * @property set {STRING} - sets the offsetAccountID
     * @memberof ACHSearch
     */
    set offsetAccountID(value) { this._offsetAccountID = value; }

    /**
     * @name transactionType
     * @instance
     * @property set {STRING} - sets the transactionType
     * @memberof ACHSearch
     */
    set transactionType(value) { this._transactionType = value; }

    /**
     * @name effectiveDate
     * @instance
     * @property set {STRING} - sets the effectiveDate
     * @memberof ACHSearch
     */
    set effectiveDate(value) { this._effectiveDate = value; }

    /**
     * @name effectiveDateFromDate
     * @instance
     * @property set {BCMSDATE} - sets the effectiveDateFromDate
     * @memberof ACHSearch
     */
    set effectiveDateFromDate(value) { this._effectiveDateFromDate = value; }

    /**
     * @name effectiveDateToDate
     * @instance
     * @property set {BCMSDATE} - sets the effectiveDateToDate
     * @memberof ACHSearch
     */
    set effectiveDateToDate(value) { this._effectiveDateToDate = value; }

    /**
     * @name lastUpdatedDate
     * @instance
     * @property set {STRING} - sets the lastUpdatedDate
     * @memberof ACHSearch
     */
    set lastUpdatedDate(value) { this._lastUpdatedDate = value; }

    /**
     * @name lastUpdatedDateFromDate
     * @instance
     * @property set {BCMSDATE} - sets the lastUpdatedDateFromDate
     * @memberof ACHSearch
     */
    set lastUpdatedDateFromDate(value) { this._lastUpdatedDateFromDate = value; }

    /**
     * @name lastUpdatedDateToDate
     * @instance
     * @property set {BCMSDATE} - sets the lastUpdatedDateToDate
     * @memberof ACHSearch
     */
    set lastUpdatedDateToDate(value) { this._lastUpdatedDateToDate = value; }
    
    /**
     * @name companyID
     * @instance
     * @property get {Map} - returns the companyID
     * @memberof ACHSearch
     */
    get companyID() { return this._companyID; }

    /**
     * @name batch
     * @instance
     * @property get {Map} - returns the batch
     * @memberof ACHSearch
     */
    get batch() { return this._batch; }

    /**
     * @name status
     * @instance
     * @property get {Map} - returns the status
     * @memberof ACHSearch
     */
    get status() { return this._status; }

    /**
     * @name prenote
     * @instance
     * @property get {Map} - returns the prenote
     * @memberof ACHSearch
     */
    get prenote() { return this._prenote; }

    /**
     * @name referenceNumber
     * @instance
     * @property get {Map} - returns the prenote
     * @memberof ACHSearch
     */
    get referenceNumber() { return this._referenceNumber; }

    /**
     * @name offsetAccountID
     * @instance
     * @property get {Map} - returns the offsetAccountID
     * @memberof ACHSearch
     */
     get offsetAccountID() { return this._offsetAccountID; }

    /**
     * @name transactionType
     * @instance
     * @property get {Map} - returns the offsetAccountID
     * @memberof ACHSearch
     */
    get transactionType() { return this._transactionType; }

    /**
     * @name effectiveDate
     * @instance
     * @property get {Map} - returns the effectiveDate
     * @memberof ACHSearch
     */
    get effectiveDate() { return this._effectiveDate; }

    /**
     * @name effectiveDateFromDate
     * @instance
     * @property get {Map} - returns the effectiveDateFromDate
     * @memberof ACHSearch
     */
    get effectiveDateFromDate() { return this._effectiveDateFromDate; }

    /**
     * @name effectiveDateToDate
     * @instance
     * @property get {Map} - returns the effectiveDateToDate
     * @memberof ACHSearch
     */
    get effectiveDateToDate() { return this._effectiveDateToDate; }

    /**
     * @name lastUpdatedDate
     * @instance
     * @property get {Map} - returns the lastUpdatedDate
     * @memberof ACHSearch
     */
    get lastUpdatedDate() { return this._lastUpdatedDate; }

    /**
     * @name lastUpdatedDateFromDate
     * @instance
     * @property get {Map} - returns the lastUpdatedDateFromDate
     * @memberof ACHSearch
     */
    get lastUpdatedDateFromDate() { return this._lastUpdatedDateFromDate; }

    /**
     * @name lastUpdatedDateToDate
     * @instance
     * @property get {Map} - returns the lastUpdatedDateToDate
     * @memberof ACHSearch
     */
    get lastUpdatedDateToDate() { return this._lastUpdatedDateToDate; }

    /**
     * @name schedule
     * @instance
     * @property get {Map} - returns the schedule
     * @memberof ACHSearch
     */
    get schedule() { return this._schedule; }

    /**
     * @name filters
     * @instance
     * @property get {OBJECT} - returns the set filter properties
     * @memberof ACHSearch
     */
    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    /*get filters() {
        return Object.getOwnPropertyNames(this).reduce((result, name) => {
            const key = name.slice(1);
            const value = this[key];

            if(value !== undefined && value !== "" && (key !== "ranges" && key !== "types")){
                result[key] = value;
            }

            return result;
        }, {});
    }*/
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof ACHSearch
     */
    get record() {
        return {
            companyID: this.companyID,
            batch: this.batch,
            schedule: this.schedule,
            status: this.status,
            prenote: this.prenote,
            referenceNumber: this.referenceNumber,
            offsetAccountID: this.offsetAccountID,
            transactionType: this.transactionType,
            effectiveDate: this.effectiveDate,
            effectiveDateFromDate: this.effectiveDateFromDate,
            effectiveDateToDate: this.effectiveDateToDate,
            lastUpdatedDate: this.lastUpdatedDate,
            lastUpdatedDateFromDate: this.lastUpdatedDateFromDate,
            lastUpdatedDateToDate: this.lastUpdatedDateToDate
        };
    }

    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    /*checkValue(transaction, key) {
        switch(key) {
            case "date":
                return transaction.date.isAfter(BCMSDate.init().offset(this.date));
    
            case "description":
                return transaction.description.toLowerCase().indexOf(this.description.toLowerCase()) > -1;
    
            case "type":
                return this.type === "all" ? true : transaction.filterType === this.type;
    
            case "amount":
                const amount = parseFloat(transaction.amount.amount);
                return parseFloat(this.amount.amount) === (amount < 0 ? -amount : amount);
    
            case "check":
                const desc = transaction.description;
                return desc.toLowerCase().indexOf("check") > -1 && desc.indexOf(this.check) > -1;
    
            default:
                return true;
        }
    }*/

    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    /*filter() {
        return Transaction.initByAccount(this.accountID)
            .filter(transaction => {
                return Object.keys(this.filters).reduce((result, key) => {
                    if(!this.checkValue(transaction, key)) {
                        result = false;
                    }

                    return result;
                }, true);
            }, []);
    }*/

    reset() {
        const vals = { 
            companyID: this.companyID,
            batch: this.batch,
            status: this.status,
            schedule: this.schedule,
            prenote: this.prenote,
            referenceNumber: this.referenceNumber,
            offsetAccountID: this.offsetAccountID,
            transactionType: this.transactionType,
            effectiveDate: this.effectiveDate,
            effectiveDateFromDate: this.effectiveDateFromDate,
            effectiveDateToDate: this.effectiveDateToDate,
            lastUpdatedDate: this.lastUpdatedDate,
            lastUpdatedDateFromDate: this.lastUpdatedDateFromDate,
            lastUpdatedDateToDate: this.lastUpdatedDateToDate
        };
        return ACHSearch.init(vals);
    }
    
    static init(record) {
        const vals = { 
            companyID: undefined,
            batch: undefined,
            schedule: undefined,
            status: undefined,
            prenote: undefined,
            referenceNumber: undefined,
            offsetAccountID: undefined,
            transactionType: undefined,
            effectiveDate: "all dates",
            effectiveDateFromDate: undefined,
            effectiveDateToDate: undefined,
            lastUpdatedDate: "all dates",
            lastUpdatedDateFromDate: undefined,
            lastUpdatedDateToDate: undefined,
            ...record ?? {} 
        }
        return new ACHSearch(vals);
    }
}